import { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, InputGroup } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';

import { PageHeading } from '../components/common/PageHeading';
import { FormAlert, SelectField, SubmitButton, TextField, vEmail, type SelectOption } from '../components/form';
import { get, post } from '../lib/api';
import { usePageTitle } from '../lib/usePageTitle';

const schema = z.object({
    email: vEmail,
    connectionString: z.string().min(1, 'validation.required'),
});
type Values = z.infer<typeof schema>;

export function ApiTokenPage() {
    const { t } = useTranslation();
    usePageTitle(t('admin.token.title'));
    const [connectionStrings, setConnectionStrings] = useState<SelectOption[]>([]);
    const [token, setToken] = useState('');
    const [serverError, setServerError] = useState('');
    const [copied, setCopied] = useState(false);

    const methods = useForm<Values>({
        resolver: zodResolver(schema),
        defaultValues: { email: '', connectionString: '' },
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });

    useEffect(() => {
        get<string[]>('apiToken')
            .then((r) => setConnectionStrings((r.data ?? []).map((d) => ({ label: d, value: d })).sort((a, b) => a.label.localeCompare(b.label, 'nb'))))
            .catch(() => {
                /* ignore */
            });
    }, []);

    async function onSubmit(values: Values) {
        setServerError('');
        setToken('');
        setCopied(false);
        try {
            const res = await post<string>('apiToken', values);
            setToken(typeof res.data === 'string' ? res.data : String(res.data));
            // Clear the email (as the legacy screen did) so the next token
            // can't be generated for the previous address by accident.
            methods.resetField('email');
        } catch {
            setServerError(t('admin.token.error'));
        }
    }

    function copyToken() {
        navigator.clipboard
            ?.writeText(token.trim())
            .then(() => setCopied(true))
            .catch(() => {
                /* clipboard may be unavailable */
            });
    }

    return (
        <>
            <PageHeading title={t('admin.token.title')} description={t('admin.token.description')} />
            <Card body className="shadow-sm" style={{ maxWidth: '36rem' }}>
                <FormProvider {...methods}>
                    <FormAlert onClose={() => setServerError('')}>{serverError || undefined}</FormAlert>
                    <Form noValidate onSubmit={methods.handleSubmit(onSubmit)}>
                        <TextField
                            name="email"
                            label={t('admin.token.email')}
                            type="email"
                            autoComplete="off"
                            required
                        />
                        <SelectField
                            name="connectionString"
                            label={t('admin.token.connectionString')}
                            options={connectionStrings}
                            required
                        />
                        <SubmitButton loading={methods.formState.isSubmitting}>
                            {t('admin.token.generate')}
                        </SubmitButton>
                    </Form>

                    {token && (
                        <div className="mt-4">
                            <Form.Label>{t('admin.token.tokenLabel')}</Form.Label>
                            <InputGroup>
                                <Form.Control as="textarea" readOnly value={token} className="font-monospace" rows={3} />
                                <Button variant="outline-secondary" type="button" onClick={copyToken}>
                                    {copied ? t('common.copied') : t('common.copy')}
                                </Button>
                            </InputGroup>
                            <Alert variant="warning" className="mt-2 mb-0 py-2 small">
                                {t('admin.token.securityNote')}
                            </Alert>
                        </div>
                    )}
                </FormProvider>
            </Card>
        </>
    );
}
